import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { generateMockPromises } from "@/lib/mock-data/collections-mock-data";
import type { PromiseToPay } from "@/types/collections";

// API Response types
interface PromiseToPayApiResponse {
  promiseId: string;
  customerId: number;
  customerName: string;
  customerAccountNumber: string | null;
  promiseDate: string;
  promisedAmount: number;
  status: string;
  confirmationMethod: string;
  createdBy: string;
  createdAt: string;
  notes: string | null;
  daysOverdue: number | null;
  isAtRisk: boolean;
  brokenPromisesCount: number;
  linkedInvoices: PromiseInvoiceApiResponse[];
}

interface PromiseInvoiceApiResponse {
  promiseInvoiceId: string;
  invoiceId: string;
  invoiceNumber: string | null;
  promisedAmount: number;
  currentStatus: string;
  balanceRemaining: number;
}

interface PromiseToPayDashboardApiResponse {
  totalPromises: number;
  pendingPromises: number;
  fulfilledPromises: number;
  brokenPromises: number;
  partialPromises: number;
  totalPromisedAmount: number;
  totalFulfilledAmount: number;
  dueToday: number;
  overdue: number;
  atRisk: number;
}

interface GetPromisesToPayApiResponse {
  data: PromiseToPayApiResponse[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  dashboard: PromiseToPayDashboardApiResponse;
}

export interface GetPromisesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface PromisesListResult {
  promises: PromiseToPay[];
  dashboard: PromiseToPayDashboardApiResponse;
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

// Map API status to UI status
function mapApiStatusToUiStatus(status: string): PromiseToPay['status'] {
  const statusMap: Record<string, PromiseToPay['status']> = {
    'pending': 'Active',
    'active': 'Active',
    'fulfilled': 'Paid',
    'broken': 'Broken',
    'partial': 'Partially Paid',
  };
  return statusMap[status.toLowerCase()] ?? 'Active';
}

// Map API confirmation method to UI confirmation method
function mapApiConfirmationMethodToUi(method: string): PromiseToPay['confirmationMethod'] {
  const methodMap: Record<string, PromiseToPay['confirmationMethod']> = {
    'phone': 'Phone',
    'email': 'Email',
    'portal': 'Portal',
  };
  return methodMap[method.toLowerCase()] ?? 'Phone';
}

// Map API response to UI model
function mapPromiseApiToUi(api: PromiseToPayApiResponse): PromiseToPay {
  return {
    id: api.promiseId,
    customerId: String(api.customerId),
    customerName: api.customerName,
    customerAccountNumber: api.customerAccountNumber ?? '',
    customerEmail: '',
    customerPhone: '',
    caseId: '',
    caseNumber: '',
    promiseDate: new Date(api.promiseDate),
    promisedAmount: api.promisedAmount,
    status: mapApiStatusToUiStatus(api.status),
    confirmationMethod: mapApiConfirmationMethodToUi(api.confirmationMethod),
    createdBy: api.createdBy,
    createdAt: new Date(api.createdAt),
    notes: api.notes ?? '',
    daysOverdue: api.daysOverdue ?? undefined,
    isAtRisk: api.isAtRisk,
    brokenPromisesCount: api.brokenPromisesCount,
    invoices: api.linkedInvoices.map(inv => ({
      id: inv.promiseInvoiceId,
      invoiceNumber: inv.invoiceNumber ?? '',
      invoiceDate: new Date(),
      originalAmount: inv.promisedAmount,
      promisedAmount: inv.promisedAmount,
      currentStatus: inv.currentStatus as 'Paid' | 'Partially Paid' | 'Unpaid',
      balanceRemaining: inv.balanceRemaining,
    })),
  };
}

// Generate mock data
const mockPromises = generateMockPromises();
const mockDashboard: PromiseToPayDashboardApiResponse = {
  totalPromises: mockPromises.length,
  pendingPromises: mockPromises.filter(p => p.status === 'Active').length,
  fulfilledPromises: mockPromises.filter(p => p.status === 'Paid').length,
  brokenPromises: mockPromises.filter(p => p.status === 'Broken').length,
  partialPromises: mockPromises.filter(p => p.status === 'Partially Paid').length,
  totalPromisedAmount: mockPromises.reduce((sum, p) => sum + p.promisedAmount, 0),
  totalFulfilledAmount: mockPromises.filter(p => p.status === 'Paid').reduce((sum, p) => sum + p.promisedAmount, 0),
  dueToday: mockPromises.filter(p => {
    const today = new Date();
    const promiseDate = new Date(p.promiseDate);
    return promiseDate.toDateString() === today.toDateString() && p.status === 'Active';
  }).length,
  overdue: mockPromises.filter(p => p.daysOverdue && p.daysOverdue > 0).length,
  atRisk: mockPromises.filter(p => p.isAtRisk).length,
};

export function usePromisesList(params?: GetPromisesParams) {
  const { apiGet } = useApi();

  // Ensure default pagination and sort parameters are always provided
  const queryParams: GetPromisesParams = {
    page: params?.page ?? 1,
    pageSize: params?.pageSize ?? 25,
    sortBy: params?.sortBy ?? 'promiseDate',
    sortOrder: params?.sortOrder ?? 'asc',
    ...params,
  };

  return useModeAwareQuery<PromisesListResult, Error>(
    ["promises", queryParams],
    async ({ signal }) => {
      const response = await apiGet<GetPromisesToPayApiResponse>("/api/v1/promises", queryParams, signal);
      return {
        promises: response.data.map(mapPromiseApiToUi),
        dashboard: response.dashboard,
        pagination: response.pagination,
      };
    },
    {
      promises: mockPromises,
      dashboard: mockDashboard,
      pagination: {
        page: 1,
        pageSize: 25,
        totalCount: mockPromises.length,
        totalPages: Math.ceil(mockPromises.length / 25),
      },
    },
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function usePromiseById(promiseId: string | null) {
  const { apiGet } = useApi();
  const mockPromise = mockPromises.find(p => p.id === promiseId) ?? null;

  return useModeAwareQuery<PromiseToPay | null, Error>(
    ["promise", promiseId],
    async ({ signal }) => {
      if (!promiseId) return null;
      const response = await apiGet<PromiseToPayApiResponse>(`/api/v1/promises/${promiseId}`, undefined, signal);
      return mapPromiseApiToUi(response);
    },
    mockPromise,
    {
      enabled: !!promiseId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export interface CreatePromiseRequest {
  customerId: number;
  promiseDate: string;
  promisedAmount: number;
  confirmationMethod: string;
  notes?: string;
}

export function useCreatePromise() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<PromiseToPay, Error, CreatePromiseRequest>(
    async (data) => {
      const response = await apiPost<PromiseToPayApiResponse>("/api/v1/promises", data);
      return mapPromiseApiToUi(response);
    },
    async (data) => {
      return {
        id: `PTP-${Date.now()}`,
        customerId: String(data.customerId),
        customerName: 'New Customer',
        customerAccountNumber: '',
        customerEmail: '',
        customerPhone: '',
        caseId: '',
        caseNumber: '',
        promiseDate: new Date(data.promiseDate),
        promisedAmount: data.promisedAmount,
        status: 'Active' as const,
        confirmationMethod: data.confirmationMethod as PromiseToPay['confirmationMethod'],
        createdBy: 'Current User',
        createdAt: new Date(),
        notes: data.notes ?? '',
        isAtRisk: false,
        invoices: [],
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["promises"] });
      },
    }
  );
}

export function useMarkPromiseAsFulfilled() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<PromiseToPay, Error, string>(
    async (promiseId) => {
      const response = await apiPost<PromiseToPayApiResponse>(`/api/v1/promises/${promiseId}/fulfill`, {});
      return mapPromiseApiToUi(response);
    },
    async (promiseId) => {
      const promise = mockPromises.find(p => p.id === promiseId);
      return promise ? { ...promise, status: 'Paid' as const } : mockPromises[0];
    },
    {
      onSuccess: (_data, promiseId) => {
        queryClient.invalidateQueries({ queryKey: ["promises"] });
        queryClient.invalidateQueries({ queryKey: ["promise", promiseId] });
      },
    }
  );
}

export function useMarkPromiseAsBroken() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<PromiseToPay, Error, { promiseId: string; notes?: string }>(
    async ({ promiseId, notes }) => {
      const response = await apiPost<PromiseToPayApiResponse>(`/api/v1/promises/${promiseId}/broken`, { notes });
      return mapPromiseApiToUi(response);
    },
    async ({ promiseId }) => {
      const promise = mockPromises.find(p => p.id === promiseId);
      return promise ? { ...promise, status: 'Broken' as const } : mockPromises[0];
    },
    {
      onSuccess: (_data, { promiseId }) => {
        queryClient.invalidateQueries({ queryKey: ["promises"] });
        queryClient.invalidateQueries({ queryKey: ["promise", promiseId] });
      },
    }
  );
}

export function usePromisesDashboard() {
  const { apiGet } = useApi();

  return useModeAwareQuery<PromiseToPayDashboardApiResponse, Error>(
    ["promises-dashboard"],
    async ({ signal }) => {
      return apiGet<PromiseToPayDashboardApiResponse>("/api/v1/promises/dashboard", undefined, signal);
    },
    mockDashboard,
    {
      staleTime: 30 * 1000,
      refetchOnMount: 'always',
    }
  );
}
